import { defineStore } from "pinia"
import { computed, ref, watch } from "vue"
import _ from "lodash"

export interface ArticleThcFilter {
    name: string
    min: number
    max: number
}

interface ArticleFilter {
    brand: string | undefined
    type: string | undefined
    species: string | undefined
    search: string | undefined
    thc: ArticleThcFilter | undefined
    priceCategory: string | undefined
    terpenes: string[]
}

const STORAGE_KEY = "article-filter"

function loadFromStorage(): ArticleFilter {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    const loadedFilter: ArticleFilter = {
        brand: undefined,
        type: undefined,
        species: undefined,
        search: undefined,
        thc: undefined,
        priceCategory: undefined,
        terpenes: [],
    }
    if (stored) {
        try {
            const parsedFilter = JSON.parse(stored)

            if (!_.isPlainObject(parsedFilter)) {
                return loadedFilter
            }

            if (typeof parsedFilter.brand === "string") {
                loadedFilter.brand = parsedFilter.brand
            }

            if (typeof parsedFilter.type === "string") {
                loadedFilter.type = parsedFilter.type
            }

            if (typeof parsedFilter.species === "string") {
                loadedFilter.species = parsedFilter.species
            }

            if (typeof parsedFilter.search === "string") {
                loadedFilter.search = parsedFilter.search
            }

            if (_.isPlainObject(parsedFilter.thc)) {
                if (
                    typeof parsedFilter.thc.name === "string" &&
                    typeof parsedFilter.thc.min === "number" &&
                    typeof parsedFilter.thc.max === "number"
                ) {
                    loadedFilter.thc = {
                        name: parsedFilter.thc.name,
                        min: parsedFilter.thc.min,
                        max: parsedFilter.thc.max,
                    }
                }
            }

            if (typeof parsedFilter.priceCategory === "string") {
                loadedFilter.priceCategory = parsedFilter.priceCategory
            }

            if (Array.isArray(parsedFilter.terpenes)) {
                for (const terpene of parsedFilter.terpenes) {
                    if (typeof terpene === "string") {
                        loadedFilter.terpenes.push(terpene)
                    }
                }
            }
        } catch (_) {
            return loadedFilter
        }
    }

    return loadedFilter
}

export const useArticleFilterStore = defineStore(STORAGE_KEY, () => {
    const stored: ArticleFilter = loadFromStorage()

    const brand = ref<string | undefined>(stored.brand)
    const type = ref<string | undefined>(stored.type)
    const species = ref<string | undefined>(stored.species)
    const search = ref<string | undefined>(stored.search)
    const thc = ref<ArticleThcFilter | undefined>(stored.thc)
    const priceCategory = ref<string | undefined>(stored.priceCategory)
    const terpenes = ref<string[]>(stored.terpenes ?? [])

    const state = computed<ArticleFilter>(() => ({
        brand: brand.value,
        type: type.value,
        species: species.value,
        search: search.value,
        thc: thc.value,
        priceCategory: priceCategory.value,
        terpenes: terpenes.value,
    }))

    watch(
        state,
        newState => {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState))
        },
        { deep: true },
    )

    return { brand, type, species, search, thc, priceCategory, terpenes }
})
